import { AlertTriangle, Shield, Mail, Database, Clock, UserX, Ban, XCircle, AlertOctagon } from 'lucide-react';

export type ErrorSeverity = 'critical' | 'warning' | 'validation';

export interface ErrorTypeConfig {
  type: ErrorSeverity;
  icon: typeof AlertTriangle;
  title: string;
  description: string;
  actionLabel?: string;
}

export const ERROR_TYPES = {
  // Authentication & Permission Errors
  DUPLICATE_EMAIL: {
    type: 'critical' as ErrorSeverity,
    icon: Mail,
    title: 'Email já cadastrado',
    description: 'Este email já está em uso no sistema.',
    actionLabel: 'Usar outro email'
  },
  PERMISSION_DENIED: {
    type: 'critical' as ErrorSeverity,
    icon: Shield,
    title: 'Acesso Negado',
    description: 'Você não tem permissão para realizar esta ação.',
    actionLabel: 'Entendi'
  },
  SESSION_EXPIRED: {
    type: 'critical' as ErrorSeverity,
    icon: Clock,
    title: 'Sessão Expirada',
    description: 'Sua sessão expirou. Por favor, faça login novamente.',
    actionLabel: 'Fazer Login'
  },
  ACCOUNT_BLOCKED: {
    type: 'critical' as ErrorSeverity,
    icon: Ban,
    title: 'Conta Bloqueada',
    description: 'Sua conta está temporariamente bloqueada.',
    actionLabel: 'Entendi'
  },
  
  // Validation Errors
  VALIDATION_FAILED: {
    type: 'validation' as ErrorSeverity,
    icon: AlertTriangle,
    title: 'Campos Inválidos',
    description: 'Por favor, corrija os campos destacados.',
    actionLabel: 'Corrigir'
  },
  REQUIRED_FIELDS: {
    type: 'validation' as ErrorSeverity,
    icon: XCircle,
    title: 'Campos Obrigatórios',
    description: 'Preencha todos os campos obrigatórios.',
    actionLabel: 'Corrigir'
  },
  INVALID_VALUE: {
    type: 'validation' as ErrorSeverity,
    icon: AlertTriangle,
    title: 'Valor Inválido',
    description: 'O valor informado não é válido.',
    actionLabel: 'Corrigir'
  },
  
  // Operation Errors
  OPERATION_FAILED: {
    type: 'critical' as ErrorSeverity,
    icon: AlertOctagon,
    title: 'Operação Falhou',
    description: 'Não foi possível completar a operação. Tente novamente.',
    actionLabel: 'Entendi'
  },
  DATABASE_ERROR: {
    type: 'critical' as ErrorSeverity,
    icon: Database,
    title: 'Erro de Banco de Dados',
    description: 'Ocorreu um erro ao acessar os dados. Tente novamente.',
    actionLabel: 'Tentar Novamente'
  },
  TIMEOUT_ERROR: {
    type: 'critical' as ErrorSeverity,
    icon: Clock,
    title: 'Tempo Esgotado',
    description: 'A operação demorou muito. Verifique sua conexão e tente novamente.',
    actionLabel: 'Tentar Novamente'
  },
  
  // Resource Errors
  RESOURCE_NOT_FOUND: {
    type: 'warning' as ErrorSeverity,
    icon: AlertTriangle,
    title: 'Recurso Não Encontrado',
    description: 'O item solicitado não foi encontrado.',
    actionLabel: 'Entendi'
  },
  DUPLICATE_RESOURCE: {
    type: 'critical' as ErrorSeverity,
    icon: XCircle,
    title: 'Registro Duplicado',
    description: 'Este registro já existe no sistema.',
    actionLabel: 'Entendi'
  },
  LIMIT_REACHED: {
    type: 'critical' as ErrorSeverity,
    icon: Ban,
    title: 'Limite Atingido',
    description: 'Você atingiu o limite máximo permitido.',
    actionLabel: 'Entendi'
  },
  
  // User Errors
  USER_NOT_FOUND: {
    type: 'critical' as ErrorSeverity,
    icon: UserX,
    title: 'Usuário Não Encontrado',
    description: 'O usuário especificado não foi encontrado.',
    actionLabel: 'Entendi'
  },
  DUPLICATE_USERNAME: {
    type: 'critical' as ErrorSeverity,
    icon: UserX,
    title: 'Nome de Usuário Indisponível',
    description: 'Este nome de usuário já está em uso.',
    actionLabel: 'Usar outro nome'
  },
  
  // Financial Errors
  INSUFFICIENT_BALANCE: {
    type: 'critical' as ErrorSeverity,
    icon: AlertOctagon,
    title: 'Saldo Insuficiente',
    description: 'A conta não possui saldo suficiente para esta operação.',
    actionLabel: 'Entendi'
  },
  SAME_ACCOUNT: {
    type: 'validation' as ErrorSeverity,
    icon: AlertTriangle,
    title: 'Contas Iguais',
    description: 'A conta de origem e destino não podem ser iguais.',
    actionLabel: 'Corrigir'
  }
} as const;

export type ErrorTypeKey = keyof typeof ERROR_TYPES;
